// Lote V-11 — Schemas para o sistema de facturación AEAT-compliant.
import { z } from 'zod';
// ─── IssuerSetting (admin) ────────────────────────────────────────────────
/**
 * Validador NIF/CIF/NIE español:
 *   • DNI: 8 díxitos + letra
 *   • NIE: X|Y|Z + 7 díxitos + letra
 *   • CIF: A|B|C|...|W + 7 díxitos + control (díxito ou letra)
 * Esta regex valida o **formato**; a verificación do díxito de control
 * faise no backend (issuer-settings lib).
 */
export const NifFormatRegex = /^([ABCDEFGHJKLMNPQRSUVW]\d{7}[A-J0-9]|\d{8}[A-Z]|[XYZ]\d{7}[A-Z])$/;
export const IssuerSettingSchema = z.object({
    id: z.string(),
    legal_name: z.string().nullable(),
    nif: z.string().nullable(),
    legal_form: z.string().nullable(),
    address_line1: z.string().nullable(),
    address_line2: z.string().nullable(),
    address_city: z.string().nullable(),
    address_province: z.string().nullable(),
    address_postal_code: z.string().nullable(),
    address_country: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    website: z.string().nullable(),
    registry_data: z.string().nullable(),
    invoice_series_prefix: z.string(),
    invoice_padding: z.number().int(),
    rectificative_series_prefix: z.string(),
    reset_annually: z.boolean(),
    default_vat_rate_bps: z.number().int(),
    email_from: z.string().nullable(),
    email_reply_to: z.string().nullable(),
    email_subject_template: z.string(),
    email_body_template: z.string().nullable(),
    pdf_logo_url: z.string().nullable(),
    pdf_footer_text: z.string().nullable(),
    pdf_primary_color: z.string(),
    is_active: z.boolean(),
    activated_at: z.string().datetime({ offset: true }).nullable(),
    updated_at: z.string().datetime({ offset: true }),
});
/** Update request — todos os campos editables. Cada un opcional para permitir
 *  patches parciais; o backend válida cohesión (ex. activar require todos). */
export const IssuerSettingUpdateSchema = z.object({
    legal_name: z.string().trim().min(1).max(200).nullable().optional(),
    nif: z
        .string()
        .trim()
        .regex(NifFormatRegex, 'NIF/CIF formato inválido')
        .nullable()
        .optional()
        .or(z.literal('')),
    legal_form: z.string().trim().max(100).nullable().optional(),
    address_line1: z.string().trim().max(200).nullable().optional(),
    address_line2: z.string().trim().max(200).nullable().optional(),
    address_city: z.string().trim().max(100).nullable().optional(),
    address_province: z.string().trim().max(100).nullable().optional(),
    address_postal_code: z
        .string()
        .trim()
        .regex(/^\d{5}$/, 'Código postal debe ter 5 díxitos')
        .nullable()
        .optional()
        .or(z.literal('')),
    address_country: z.string().trim().max(100).optional(),
    email: z.string().trim().email('Email non válido').nullable().optional().or(z.literal('')),
    phone: z.string().trim().max(30).nullable().optional(),
    website: z.string().trim().url('URL non válida').nullable().optional().or(z.literal('')),
    registry_data: z.string().trim().max(1000).nullable().optional(),
    invoice_series_prefix: z.string().trim().min(1).max(20).optional(),
    invoice_padding: z.number().int().min(1).max(12).optional(),
    rectificative_series_prefix: z.string().trim().min(1).max(20).optional(),
    reset_annually: z.boolean().optional(),
    default_vat_rate_bps: z.number().int().min(0).max(10000).optional(),
    email_from: z.string().trim().max(200).nullable().optional(),
    email_reply_to: z
        .string()
        .trim()
        .email('Email non válido')
        .nullable()
        .optional()
        .or(z.literal('')),
    email_subject_template: z.string().trim().min(1).max(200).optional(),
    email_body_template: z.string().max(20_000).nullable().optional(),
    pdf_logo_url: z.string().trim().max(500).nullable().optional(),
    pdf_footer_text: z.string().max(1000).nullable().optional(),
    pdf_primary_color: z
        .string()
        .trim()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Color debe ser #RRGGBB')
        .optional(),
});
/** Resposta do endpoint `/admin/issuer-settings/activation-check` — di se
 *  o singleton ten todos os campos requeridos para activar a produción. */
export const IssuerActivationCheckResponseSchema = z.object({
    ready: z.boolean(),
    missing_fields: z.array(z.string()),
    is_active: z.boolean(),
    activated_at: z.string().datetime({ offset: true }).nullable(),
});
export const IssuerActivateRequestSchema = z.object({
    confirm: z.literal(true),
});
// ─── Invoice (admin contabilidade + user "as miñas facturas") ─────────────
export const InvoiceTypeSchema = z.enum(['STANDARD', 'RECTIFICATIVE']);
export const InvoiceRecipientRoleSchema = z.enum(['SELLER', 'BUYER']);
export const InvoiceEmailStatusSchema = z.enum(['PENDING', 'SENT', 'BOUNCED', 'FAILED', 'SKIPPED']);
export const InvoiceListItemSchema = z.object({
    id: z.string(),
    full_number: z.string(),
    invoice_type: InvoiceTypeSchema,
    issued_at: z.string().datetime({ offset: true }),
    operation_date: z.string().datetime({ offset: true }),
    recipient_user_id: z.string(),
    recipient_name: z.string(),
    recipient_nif: z.string().nullable(),
    recipient_role: InvoiceRecipientRoleSchema,
    concept: z.string(),
    base_cents: z.number().int(),
    vat_rate_bps: z.number().int(),
    vat_cents: z.number().int(),
    total_cents: z.number().int(),
    currency: z.string(),
    email_status: InvoiceEmailStatusSchema,
    email_sent_at: z.string().datetime({ offset: true }).nullable(),
    read_at: z.string().datetime({ offset: true }).nullable(),
    voided_at: z.string().datetime({ offset: true }).nullable(),
    corrects_invoice_id: z.string().nullable(),
    corrects_invoice_number: z.string().nullable(),
    has_pdf: z.boolean(),
});
export const InvoiceListQuerySchema = z.object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    type: InvoiceTypeSchema.optional(),
    recipient_role: InvoiceRecipientRoleSchema.optional(),
    email_status: InvoiceEmailStatusSchema.optional(),
    search: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().positive().default(1),
    page_size: z.coerce.number().int().positive().max(200).default(50),
});
export const InvoiceListResponseSchema = z.object({
    items: z.array(InvoiceListItemSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    page_size: z.number().int().positive(),
});
/** Stats resumo do header da pantalla Contabilidade. */
export const InvoiceStatsResponseSchema = z.object({
    current_month: z.object({
        count: z.number().int().nonnegative(),
        base_cents: z.number().int(),
        vat_cents: z.number().int(),
        total_cents: z.number().int(),
    }),
    current_quarter: z.object({
        quarter: z.number().int().min(1).max(4),
        year: z.number().int(),
        count: z.number().int().nonnegative(),
        base_cents: z.number().int(),
        vat_cents: z.number().int(),
        total_cents: z.number().int(),
    }),
    current_fiscal_year: z.object({
        year: z.number().int(),
        count: z.number().int().nonnegative(),
        base_cents: z.number().int(),
        vat_cents: z.number().int(),
        total_cents: z.number().int(),
    }),
    email_breakdown: z.object({
        PENDING: z.number().int().nonnegative(),
        SENT: z.number().int().nonnegative(),
        BOUNCED: z.number().int().nonnegative(),
        FAILED: z.number().int().nonnegative(),
        SKIPPED: z.number().int().nonnegative(),
    }),
});
/** Resposta de GET /users/me/invoices/:id/download. */
export const InvoiceDownloadResponseSchema = z.object({
    url: z.string(),
    expires_in_seconds: z.number().int().positive(),
});
/** Resposta de PATCH /users/me/invoices/:id/read. */
export const InvoiceMarkReadResponseSchema = z.object({
    id: z.string(),
    read_at: z.string().datetime({ offset: true }),
});
/** Request: POST /admin/invoices/:id/void → emite rectificativa. */
export const InvoiceVoidRequestSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
/** Resposta: devolve a rectificativa creada (full_number + id). */
export const InvoiceVoidResponseSchema = z.object({
    rectificative_id: z.string(),
    rectificative_number: z.string(),
});
/** Query string para os exports CSV/XLSX/ZIP. */
export const InvoiceExportQuerySchema = z.object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    type: InvoiceTypeSchema.optional(),
});
/** Resposta de POST /admin/invoices/:id/resend-email. */
export const InvoiceResendEmailResponseSchema = z.object({
    id: z.string(),
    email_status: z.string(),
});
/** Detalle dunha factura (admin clic en fila). Inclúe snapshots. */
export const InvoiceDetailResponseSchema = InvoiceListItemSchema.extend({
    issuer_snapshot: z.unknown(),
    recipient_snapshot: z.unknown(),
    marketplace_sale_id: z.string().nullable(),
    rectification_reason: z.string().nullable(),
    email_error: z.string().nullable(),
    email_attempts: z.number().int().nonnegative(),
});
//# sourceMappingURL=invoicing.js.map